import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import notificationsApi, { AUDIENCE } from 'api/requests/notifications';
import { useAuth } from 'context/auth/Auth';
import { useUser } from 'context/user/User';
import { hasRole } from 'helpers/dashboardAccess';
import ROLES from 'helpers/roles';

// A lista marca tudo como lido ao abrir; o sino, que fica na mesma tela,
// escuta este evento para zerar na hora (mesmo padrão do `auth-token-removed`).
// O evento diz de qual feed foi: o sino do outro público não zera junto.
const NOTIFICATIONS_READ_EVENT = 'notifications-read';

export function announceNotificationsRead(audience = AUDIENCE.CONSUMER) {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_READ_EVENT, { detail: { audience } }));
}

/**
 * Qual feed é o da conta: o papel decide. Cliente final tem o do comprador;
 * produtor, o do painel, que aparece em qualquer header (também nas páginas
 * públicas, onde ele navega como qualquer um). Estabelecimento também cria
 * eventos e abre o painel do produtor, e a API já grava os avisos de venda e
 * financeiro para o dono do evento, qualquer que seja o papel dele: ganha o
 * mesmo feed do produtor. Sem feed (visitante, admin — fica para depois),
 * `null`: sem sino.
 */
export function notificationAudience(user) {
    if (!user?.id) return null;
    if (hasRole(user, [ROLES.USER])) return AUDIENCE.CONSUMER;
    if (hasRole(user, [ROLES.PRODUCER, ROLES.ESTABLISHMENT])) return AUDIENCE.PRODUCER;

    return null;
}

/**
 * Estado do sino dos headers: `enabled` diz se ele aparece, `audience` de qual
 * feed ele é e `unreadCount` alimenta o contador.
 *
 * O número é consultado quando o sino monta, a cada troca de página e quando a
 * janela volta a ter foco — é assim que aparece o que chegou com a aba aberta
 * em segundo plano. Quem zera é a lista, ao abrir (ver `announceNotificationsRead`).
 */
export default function useNotificationBell() {
    const location = useLocation();
    const { isAuth } = useAuth();
    const { user } = useUser();
    const audience = isAuth ? notificationAudience(user) : null;
    const enabled = audience !== null;

    const [unreadCount, setUnreadCount] = useState(0);
    // Resposta que chega depois de a lista zerar o contador está velha: cada
    // consulta guarda o seu número e só vale se nada aconteceu desde então.
    const latestRequest = useRef(0);

    useEffect(() => {
        if (!enabled) {
            setUnreadCount(0);
            return undefined;
        }

        let active = true;

        function refresh() {
            const request = ++latestRequest.current;

            notificationsApi.unreadCount(audience)
                .then(({ data }) => {
                    if (active && request === latestRequest.current) {
                        setUnreadCount(Math.max(0, Number(data?.unread) || 0));
                    }
                })
                // Sem o número, fica sem contador — o sino continua levando à lista.
                .catch(() => {});
        }

        function clear(event) {
            if (event.detail?.audience !== audience) return;

            latestRequest.current++;
            setUnreadCount(0);
        }

        refresh();
        window.addEventListener('focus', refresh);
        window.addEventListener(NOTIFICATIONS_READ_EVENT, clear);

        return () => {
            active = false;
            window.removeEventListener('focus', refresh);
            window.removeEventListener(NOTIFICATIONS_READ_EVENT, clear);
        };
    }, [enabled, audience, location.pathname]);

    return { enabled, audience, unreadCount };
}
